import { observer } from 'mobx-react';
import React, { type FC, useState } from 'react';
import { useStores } from '../../utils/hooks/useStores';
import AppErrorBoundary from '../app/AppErrorBoundary';
import ListResultView from './ListResultView';
import * as styles from './index.css';
import {
  Alert,
  Box,
  Column,
  Columns,
  ContentBlock,
  Text,
} from 'braid-design-system';
import { FilterSideBar } from '../app/FilterSideBar/FilterSideBar';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';
import { BUCKETS } from 'src/stores/JobApplicationStore/types';
import { isANZSite, isJobStreetSite } from 'src/utils/site';
import format from 'date-fns/format';
import { languageToLocaleMap } from '../common/TimeAgo/timeUtils';
import type { Locale as MelwaysLocale } from '@seek/melways-sites';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

const JobsDBJobsWithoutMigrationDateObject = new Date('2021-12-31');
const JobStreetJobsWithoutMigrationDateObject = new Date('2022-10-01');

const formatDateInLocale = ({
  date,
  locale,
}: {
  date: Date;
  locale: MelwaysLocale;
}) =>
  format(date, 'dd MMM yyyy', {
    locale: languageToLocaleMap[locale],
  });

const BannerComponent = ({
  site,
  locale,
}: {
  site: string;
  locale: MelwaysLocale;
}) => {
  const [shouldShowAlert, setShouldShowAlert] = useState(true);
  const { t } = useTranslations(translations);

  if (!shouldShowAlert) {
    return null;
  }

  return (
    <Box paddingY="medium">
      <Alert
        tone="info"
        onClose={() => setShouldShowAlert(false)}
        closeLabel="close-missing-application-alert"
      >
        <Text>
          {t('Applications received before message', {
            formattedDate: isJobStreetSite(site)
              ? formatDateInLocale({
                  date: JobStreetJobsWithoutMigrationDateObject,
                  locale,
                })
              : formatDateInLocale({
                  date: JobsDBJobsWithoutMigrationDateObject,
                  locale,
                }),
          })}
        </Text>
      </Alert>
    </Box>
  );
};

interface CandidateListProps {
  isStickyOnDesktop: boolean;
  openBillableDialog: () => void;
}

export const CandidateList: FC<CandidateListProps> = ({
  isStickyOnDesktop,
  openBillableDialog,
}) => {
  const store = useStores();

  const { jobApplicationStore, uiStore, isMobile, jobStore } = store;
  const {
    job: {
      jobInformation: { questionnaireId },
    },
  } = jobStore;
  const { site, locale } = useAppConfig();

  const getMigrationCutOffDate = () => {
    if (isJobStreetSite(site)) {
      return JobStreetJobsWithoutMigrationDateObject;
    }
    return JobsDBJobsWithoutMigrationDateObject;
  };

  const migrationCutOffDate = getMigrationCutOffDate();

  const isMigrationAffected =
    !isANZSite(site) &&
    new Date(jobStore.job.jobInformation.positionCreatedDate) <
      migrationCutOffDate;

  const scrollIntoViewOnScroll = (event: any) => {
    const target = event?.nativeEvent?.target;
    if (target) {
      const { top } = target.getBoundingClientRect();

      if (top > 0) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const bucket = jobApplicationStore.bucket;

  return (
    <AppErrorBoundary>
      <Box
        data-cy="main-content"
        marginX={['none', 'xxsmall']}
        background="surface"
      >
        <Columns space={['none', 'none', 'small']}>
          <Column width="content">
            <Box
              paddingBottom="medium"
              className={{
                [styles.sticky]: true,
                [styles.stickyBelowTab]: isStickyOnDesktop,
                [styles.sideBarBorder]: true,
              }}
            >
              <Box className={[styles.hideOnTablet]}>
                <Box
                  className={styles.filterSideBar}
                  onScroll={scrollIntoViewOnScroll}
                >
                  <FilterSideBar />
                </Box>
              </Box>
            </Box>
          </Column>

          <Column>
            <ContentBlock width="large">
              {bucket === BUCKETS.INBOX && isMigrationAffected && (
                <BannerComponent site={site} locale={locale} />
              )}
              <ListResultView
                jobApplicationStore={jobApplicationStore}
                isMobile={isMobile}
                questionnaireId={questionnaireId}
                uiStore={uiStore}
                openBillableDialog={openBillableDialog}
              />
            </ContentBlock>
          </Column>
        </Columns>
      </Box>
    </AppErrorBoundary>
  );
};

export default observer(CandidateList);
